import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2, Pencil, Save, X, Upload, ImageOff, ImageIcon, Link as LinkIcon, CalendarDays, Bell, BellRing, Loader2, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { NotificationPreview } from "@/components/NotificationPreview";

const empty = {
  title_es: "",
  title_en: "",
  body_es: "",
  body_en: "",
  image_url: "",
  registration_url: "",
  news_date: "",
  published: true,
  push_schedule_local: "",
};

const MAX_BYTES = 8 * 1024 * 1024;

/** Interpolate `{n}` or `{when}` in an i18n template with the given value. */
function interp(template, val) {
  if (template == null) return String(val);
  const s = String(val);
  return template.replace("{n}", s).replace("{when}", s);
}
function fmtSentCount(n, tNews) {
  const tpl = n === 1 ? tNews.pushSentCount : tNews.pushSentCountPlural || tNews.pushSentCount;
  return interp(tpl, n);
}
function fmtPushSuccess(n, tNews) {
  const tpl = n === 1 ? tNews.pushSuccess : tNews.pushSuccessPlural || tNews.pushSuccess;
  return interp(tpl, n);
}

export default function AdminNews() {
  const { t, lang } = useI18n();
  const tNews = t.admin.newsForm;
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // When set, this is the news item currently being confirmed for re-send.
  // When null, no confirmation is pending. Non-null triggers the AlertDialog.
  const [confirmResend, setConfirmResend] = useState(null);
  // id -> boolean; while true we disable the button and show a spinner.
  const [pushLoadingById, setPushLoadingById] = useState({});
  // Local toggle for "send push on publish" — only meaningful when creating
  // a new item (i.e. ``editing?.id`` is undefined). We reset it every time
  // the dialog opens so a stale checked state can't quietly resend on a
  // subsequent edit.
  const [sendPushOnCreate, setSendPushOnCreate] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/news");
      setList(data);
    } catch (err) {
      console.error("AdminNews: failed to load news list", err);
    }
  };
  useEffect(() => { reload(); }, []);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t.admin.newsForm.invalidImage);
      e.target.value = "";
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t.admin.newsForm.tooLarge);
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = (typeof localStorage !== "undefined" && localStorage.getItem("ncl_admin_token")) || "";
      const res = await fetch(`${API_BASE}/admin/news/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error("upload failed");
      const data = await res.json();
      setEditing((cur) => ({ ...cur, image_url: data.image_url }));
      toast.success(t.admin.saved);
    } catch (_) {
      toast.error(t.common.error);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSave = async () => {
    try {
      // Normalize the news_date: send null when empty so the backend
      // stores None rather than an empty string. Backend accepts either
      // but this keeps the DB tidy.
      const payload = { ...editing, news_date: editing.news_date || null };
      // Same treatment for the scheduled-push field. Empty string means
      // "cancel the schedule"; the backend interprets that explicitly.
      payload.push_schedule_local = editing.push_schedule_local || null;
      // Strip UI-only / server-owned fields the backend doesn't accept in
      // the news body (NewsIn ignores them but this keeps the wire tidy).
      delete payload.push_sent_at;
      delete payload.push_sent_count;
      delete payload.push_scheduled_for;
      delete payload.push_schedule_tz;
      delete payload.push_schedule_status;
      delete payload.push_schedule_error;
      if (editing.id) {
        // Editing existing item — never auto-send push on edit; that's
        // reserved for the dedicated "Send push" button per item.
        await api.put(`/admin/news/${editing.id}`, payload);
      } else {
        // Creating new item — honor the toggle. The query param is what
        // the backend inspects (see ``admin_create_news(send_push=...)``).
        const url = sendPushOnCreate ? "/admin/news?send_push=true" : "/admin/news";
        await api.post(url, payload);
      }
      toast.success(t.admin.saved);
      setOpen(false);
      setSendPushOnCreate(false);
      reload();
    } catch (e) {
      // Surface the backend's specific 400 (e.g. "Scheduled time must be
      // at least 30 seconds in the future") when available.
      const detail = e?.response?.data?.detail;
      toast.error(detail ? String(detail) : t.common.error);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    try {
      await api.delete(`/admin/news/${id}`);
      toast.success(t.admin.deleted);
      reload();
    } catch (_) {
      toast.error(t.common.error);
    }
  };

  /**
   * Fire the "send push" API for a specific news item.
   *
   * If the item was already sent at least once, we route the request
   * through :func:`confirmResend` (an AlertDialog) so a stray click on the
   * bell button can't accidentally spam members. Otherwise (first send),
   * we skip the confirmation for a quicker one-tap experience.
   */
  const doSendPush = async (item) => {
    setPushLoadingById((m) => ({ ...m, [item.id]: true }));
    try {
      const { data } = await api.post(`/admin/news/${item.id}/push`);
      const sent = typeof data?.sent === "number" ? data.sent : 0;
      toast.success(fmtPushSuccess(sent, tNews));
      reload();
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || t.common.error));
    } finally {
      setPushLoadingById((m) => {
        const next = { ...m };
        delete next[item.id];
        return next;
      });
      setConfirmResend(null);
    }
  };

  const requestSendPush = (item) => {
    if ((item.push_sent_count || 0) > 0) {
      setConfirmResend(item);
    } else {
      doSendPush(item);
    }
  };

  /**
   * Minimum value for the schedule datetime-local input.
   *
   * We compute "current wall clock in America/Los_Angeles" so admins can't
   * pick a time that already happened. Add 2 minutes of buffer so a slow
   * network round-trip doesn't fail with the backend's 30-second lower
   * bound. The result is a naive local ``YYYY-MM-DDTHH:MM`` string that
   * the browser accepts as the ``min`` attribute.
   */
  const scheduleMin = useMemo(() => {
    const d = new Date(Date.now() + 2 * 60 * 1000);
    // ``en-CA`` gives "2026-01-30, 18:00" style output which is easy to
    // reformat and — crucially — accepts a ``timeZone`` option so the
    // string reflects Pacific wall clock, not the admin's browser tz.
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const pick = (type) => parts.find((p) => p.type === type)?.value || "";
    const hour = pick("hour") === "24" ? "00" : pick("hour"); // Intl edge-case
    return `${pick("year")}-${pick("month")}-${pick("day")}T${hour}:${pick("minute")}`;
  }, [open]); // Recompute whenever the dialog opens.

  /** Human-readable schedule status pill for a news card. */
  const renderScheduleBadge = (n) => {
    const status = n.push_schedule_status;
    if (!status || status === "sent" || status === "canceled") return null;
    const when = n.push_schedule_local
      ? fmtDateTime(n.push_scheduled_for || n.push_schedule_local, lang)
      : "";
    if (status === "scheduled") {
      return (
        <Badge
          variant="secondary"
          className="gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[10px]"
          data-testid={`admin-news-schedule-status-${n.id}`}
        >
          <Clock className="h-3 w-3" />
          {interp(tNews.scheduleStatusScheduled, when)}
        </Badge>
      );
    }
    if (status === "sending") {
      return (
        <Badge variant="secondary" className="gap-1 rounded-full bg-blue-100 text-blue-800 border border-blue-200 text-[10px]">
          <Loader2 className="h-3 w-3 animate-spin" />
          {tNews.scheduleStatusSending}
        </Badge>
      );
    }
    if (status === "failed") {
      return (
        <Badge variant="destructive" className="gap-1 rounded-full text-[10px]" title={n.push_schedule_error || ""}>
          <AlertTriangle className="h-3 w-3" />
          {tNews.scheduleStatusFailed}
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-display text-2xl font-semibold">{t.admin.news}</h1>
        <Button
          onClick={() => {
            setEditing({ ...empty });
            setSendPushOnCreate(false);
            setOpen(true);
          }}
          data-testid="admin-news-add"
        >
          <Plus className="mr-1 h-4 w-4" />{t.admin.addNew}
        </Button>
      </div>
      {list.length === 0 ? (
        <Card className="rounded-2xl border-dashed bg-white/60">
          <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="admin-news-empty">{t.admin.noItems}</CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="admin-news-list">
          {list.map((n) => {
            const sentCount = n.push_sent_count || 0;
            const alreadySent = sentCount > 0;
            const pushBusy = !!pushLoadingById[n.id];
            return (
              <Card key={n.id} className="rounded-2xl ring-1 ring-black/5">
                <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    {n.image_url ? (
                      <img
                        src={n.image_url}
                        alt=""
                        className="h-16 w-16 shrink-0 rounded-lg object-cover ring-1 ring-black/5"
                        data-testid={`admin-news-thumb-${n.id}`}
                      />
                    ) : (
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground ring-1 ring-black/5">
                        <ImageOff className="h-5 w-5" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-serif-display text-base font-semibold">{lang === "es" ? n.title_es : n.title_en}</p>
                      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                        {n.news_date ? (
                          <>
                            <Badge variant="secondary" className="gap-1 rounded-full text-[10px]">
                              <CalendarDays className="h-3 w-3" />
                              {fmtDate(n.news_date, lang)}
                            </Badge>
                            <span className="text-muted-foreground/70">
                              {lang === "es" ? "publicado" : "posted"} {fmtDate(n.created_at, lang)}
                            </span>
                          </>
                        ) : (
                          <span>{fmtDate(n.created_at, lang)}</span>
                        )}
                        {/* Push status pill: green when at least once sent,
                            neutral when never sent. Only rendered inline
                            below the date row so it doesn't take up its
                            own vertical block on the card. */}
                        {alreadySent ? (
                          <Badge
                            variant="secondary"
                            className="gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]"
                            data-testid={`admin-news-push-status-${n.id}`}
                          >
                            <BellRing className="h-3 w-3" />
                            {fmtSentCount(sentCount, tNews)}
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="gap-1 rounded-full text-[10px]"
                            data-testid={`admin-news-push-status-${n.id}`}
                          >
                            <Bell className="h-3 w-3" />
                            {tNews.pushNeverSentBadge}
                          </Badge>
                        )}
                        {renderScheduleBadge(n)}
                      </div>
                      {alreadySent && n.push_sent_at && (
                        <p className="mt-0.5 text-[10px] text-muted-foreground/80">
                          {tNews.pushLastSent}: {fmtDateTime(n.push_sent_at, lang)}
                        </p>
                      )}
                      <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{lang === "es" ? n.body_es : n.body_en}</p>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-1 sm:flex-nowrap">
                    <Button
                      size="sm"
                      variant={alreadySent ? "outline" : "default"}
                      onClick={() => requestSendPush(n)}
                      disabled={pushBusy || !n.published}
                      data-testid={`admin-news-send-push-${n.id}`}
                    >
                      {pushBusy ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Bell className="h-4 w-4" />
                      )}
                      <span className="ml-1 hidden sm:inline">
                        {pushBusy ? tNews.sendPushSending : tNews.sendPushButton}
                      </span>
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditing(n); setSendPushOnCreate(false); setOpen(true); }} data-testid={`admin-news-edit-${n.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onDelete(n.id)} data-testid={`admin-news-delete-${n.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-news-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? t.common.edit : t.common.create} - {t.admin.news}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t.admin.newsForm.title_es}>
                  <Input value={editing.title_es} onChange={(e) => setEditing({ ...editing, title_es: e.target.value })} data-testid="news-title-es" />
                </Field>
                <Field label={t.admin.newsForm.title_en}>
                  <Input value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} data-testid="news-title-en" />
                </Field>
              </div>

              {/* Optional "intended date" for the news item. If left blank
                  the public UI falls back to the post timestamp. */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-primary" />
                  <Label className="text-xs font-medium">
                    {lang === "es" ? "Fecha de la noticia (opcional)" : "News date (optional)"}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={editing.news_date || ""}
                    onChange={(e) => setEditing({ ...editing, news_date: e.target.value })}
                    className="max-w-[16rem]"
                    data-testid="news-news-date"
                  />
                  {editing.news_date && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing({ ...editing, news_date: "" })}
                      data-testid="news-news-date-clear"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {lang === "es"
                    ? "Fecha para la que esta la noticia (ej. el domingo del servicio). Si la dejas vacia, se muestra la fecha en que se publico."
                    : "The date the announcement is FOR (e.g., the Sunday of the service). Leave blank to show the post date instead."}
                </p>
              </div>

              <Field label={t.admin.newsForm.body_es}>
                <Textarea rows={4} value={editing.body_es} onChange={(e) => setEditing({ ...editing, body_es: e.target.value })} data-testid="news-body-es" />
              </Field>
              <Field label={t.admin.newsForm.body_en}>
                <Textarea rows={4} value={editing.body_en} onChange={(e) => setEditing({ ...editing, body_en: e.target.value })} data-testid="news-body-en" />
              </Field>

              {/* Flyer upload section */}
              <div className="space-y-2 rounded-xl border border-dashed border-primary/30 bg-primary/5 p-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <ImageIcon className="h-4 w-4 text-primary" />
                  {t.admin.newsForm.image}
                </div>
                <p className="text-xs text-muted-foreground">{t.admin.newsForm.imageHint}</p>

                {editing.image_url && (
                  <div className="relative overflow-hidden rounded-lg ring-1 ring-black/5">
                    <img
                      src={editing.image_url}
                      alt=""
                      className="block max-h-72 w-full object-contain bg-muted"
                      data-testid="news-image-preview"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      className="absolute right-2 top-2 h-8 gap-1"
                      onClick={() => setEditing({ ...editing, image_url: "" })}
                      data-testid="news-image-remove"
                    >
                      <X className="h-3.5 w-3.5" />
                      {t.admin.newsForm.removeImage}
                    </Button>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="news-image-upload-btn"
                  >
                    <Upload className="mr-1 h-4 w-4" />
                    {uploading ? t.admin.newsForm.uploading : t.admin.newsForm.uploadFromDevice}
                  </Button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onPickFile}
                    data-testid="news-image-file-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">{t.admin.newsForm.pasteUrl}</Label>
                  <Input
                    value={editing.image_url?.startsWith("data:") ? "" : (editing.image_url || "")}
                    onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="news-image-url"
                  />
                </div>
              </div>

              {/* Registration URL */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-primary" />
                  <Label className="text-xs font-medium">{t.admin.newsForm.registrationUrl}</Label>
                </div>
                <Input
                  type="url"
                  inputMode="url"
                  placeholder="https://forms.google.com/..."
                  value={editing.registration_url || ""}
                  onChange={(e) => setEditing({ ...editing, registration_url: e.target.value })}
                  data-testid="news-registration-url"
                />
                <p className="text-[11px] text-muted-foreground">{t.admin.newsForm.registrationUrlHint}</p>
              </div>

              {/* "Send push on publish" toggle: only offered when creating a
                  brand-new news item. On edits, this row is hidden because
                  updating an existing news doesn't (and shouldn't) fire a
                  fresh push — that's the dedicated "Send notification"
                  button per card. */}
              {!editing.id && (
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Bell className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <Label className="text-sm font-medium" htmlFor="news-send-push-toggle">
                          {t.admin.newsForm.sendPushOnCreate}
                        </Label>
                        <Switch
                          id="news-send-push-toggle"
                          checked={sendPushOnCreate}
                          onCheckedChange={setSendPushOnCreate}
                          data-testid="news-send-push-toggle"
                        />
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {t.admin.newsForm.sendPushOnCreateHint}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Phase 13 — Scheduled Push. Available for both create and
                  edit so admins can arm, re-arm, or clear a scheduled push
                  on any news item. Interpreted as America/Los_Angeles
                  (Pacific) on the backend. */}
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 space-y-2">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-200/60 text-amber-800">
                    <Clock className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <Label className="text-sm font-medium" htmlFor="news-schedule-input">
                      {tNews.scheduleLabel}
                    </Label>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {tNews.scheduleHint}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    id="news-schedule-input"
                    type="datetime-local"
                    value={editing.push_schedule_local || ""}
                    min={scheduleMin}
                    onChange={(e) => setEditing({ ...editing, push_schedule_local: e.target.value })}
                    className="max-w-[16rem]"
                    data-testid="news-schedule-input"
                  />
                  {editing.push_schedule_local && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing({ ...editing, push_schedule_local: "" })}
                      data-testid="news-schedule-clear"
                    >
                      <X className="mr-1 h-4 w-4" />
                      {tNews.scheduleClear}
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {tNews.scheduleTzNote}
                  {editing.id && editing.push_schedule_status === "scheduled" ? (
                    <> · {tNews.scheduleCanceledOnEdit}</>
                  ) : null}
                </p>
              </div>

              {/* Phase 14 — Live iPhone lock-screen preview with ES/EN
                  language toggle. Updates as the admin types. */}
              <NotificationPreview
                titleEs={editing.title_es}
                titleEn={editing.title_en}
                defaultLang={lang}
              />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              <X className="mr-1 h-4 w-4" />{t.common.cancel}
            </Button>
            <Button onClick={onSave} disabled={uploading} data-testid="admin-news-save">
              <Save className="mr-1 h-4 w-4" />{t.common.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-send confirmation. Fires when the admin taps the bell on a
          news item that was already pushed at least once. */}
      <AlertDialog open={!!confirmResend} onOpenChange={(v) => !v && setConfirmResend(null)}>
        <AlertDialogContent data-testid="admin-news-resend-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{tNews.confirmResendTitle}</AlertDialogTitle>
            <AlertDialogDescription>{tNews.confirmResendBody}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.common.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmResend && doSendPush(confirmResend)}
              data-testid="admin-news-resend-confirm-cta"
            >
              {tNews.confirmResendCta}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{label}</Label>
      {children}
    </div>
  );
}
